import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Company {
  id: string;
  name: string;
}

interface CompanySelectorProps {
  onSelectCompany: (id: string, name: string) => void;
  selectedCompanyId?: string;
  required?: boolean;
}

export const CompanySelector = ({ onSelectCompany, selectedCompanyId, required = false }: CompanySelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (open) {
        setIsLoading(true);
        setError(null);
        
        try {
          let query = supabase
            .from('companies')
            .select('id, name');
          
          if (searchTerm) {
            query = query.ilike('name', `%${searchTerm}%`);
          }
          
          const { data, error } = await query.limit(10);
          
          if (error) {
            console.error("Error fetching companies:", error);
            setError("Failed to load companies");
            setCompanies([]);
            return;
          }
          
          setCompanies(data || []);
        } catch (error) {
          console.error("Failed to fetch companies:", error);
          setError("An unexpected error occurred");
          setCompanies([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCompanies();
  }, [searchTerm, open]);

  useEffect(() => {
    const fetchSelectedCompany = async () => {
      if (selectedCompanyId) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('id, name')
            .eq('id', selectedCompanyId)
            .single();
          
          if (error) {
            console.error("Error fetching selected company:", error);
            return;
          }
          
          if (data) {
            setSelectedCompany(data);
            setSearchTerm(data.name);
          }
        } catch (error) {
          console.error("Failed to fetch selected company:", error);
        }
      }
    };

    if (!selectedCompany && selectedCompanyId) {
      fetchSelectedCompany();
    }
  }, [selectedCompanyId, selectedCompany]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          aria-required={required}
        >
          {selectedCompany?.name || searchTerm || "Select company..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput 
            placeholder="Search companies..." 
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Loading companies...
              </div>
            ) : error ? (
              <div className="py-6 text-center text-sm text-red-500">
                {error}
              </div>
            ) : (
              <>
                <CommandEmpty>No companies found.</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-auto">
                  {companies.map((company) => (
                    <CommandItem
                      key={company.id}
                      value={company.name}
                      onSelect={() => {
                        onSelectCompany(company.id, company.name);
                        setSelectedCompany(company);
                        setSearchTerm(company.name);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCompanyId === company.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {company.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
